import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid';

export interface HouseholdMember {
  id: string;
  name: string;
  monthlyIncome: number;
  expenses: Array<{
    id: string;
    name: string;
    amount: number;
    category: 'needs' | 'wants' | 'savings';
    isShared: boolean;
    contributionRatio?: number;
  }>;
  savingsGoals: Array<{
    id: string;
    name: string;
    targetAmount: number;
    currentAmount: number;
    deadline: string;
    isJoint: boolean;
  }>;
  debts: Array<{
    id: string;
    name: string;
    amount: number;
    interestRate: number;
    minimumPayment: number;
    isJoint: boolean;
  }>;
}

interface HouseholdState {
  members: HouseholdMember[];
  addMember: (member: Omit<HouseholdMember, 'id'>) => void;
  updateMember: (id: string, updates: Partial<HouseholdMember>) => void;
  removeMember: (id: string) => void;
  addExpense: (memberId: string, expense: Omit<HouseholdMember['expenses'][0], 'id'>) => void;
  updateExpense: (memberId: string, expenseId: string, updates: Partial<HouseholdMember['expenses'][0]>) => void;
  removeExpense: (memberId: string, expenseId: string) => void;
  addSavingsGoal: (memberId: string, goal: Omit<HouseholdMember['savingsGoals'][0], 'id'>) => void;
  updateSavingsGoal: (memberId: string, goalId: string, updates: Partial<HouseholdMember['savingsGoals'][0]>) => void;
  removeSavingsGoal: (memberId: string, goalId: string) => void;
  addDebt: (memberId: string, debt: Omit<HouseholdMember['debts'][0], 'id'>) => void;
  updateDebt: (memberId: string, debtId: string, updates: Partial<HouseholdMember['debts'][0]>) => void;
  removeDebt: (memberId: string, debtId: string) => void;
}

export const useHouseholdStore = create<HouseholdState>()(
  persist(
    (set) => ({
      members: [],
      
      addMember: (member) => set((state) => ({
        members: [...state.members, { ...member, id: nanoid() }]
      })),
      
      updateMember: (id, updates) => set((state) => ({
        members: state.members.map(member =>
          member.id === id ? { ...member, ...updates } : member
        )
      })),
      
      removeMember: (id) => set((state) => ({
        members: state.members.filter(member => member.id !== id)
      })),
      
      addExpense: (memberId, expense) => set((state) => ({
        members: state.members.map(member =>
          member.id === memberId
            ? {
                ...member,
                expenses: [...member.expenses, { ...expense, id: nanoid() }]
              }
            : member
        )
      })),
      
      updateExpense: (memberId, expenseId, updates) => set((state) => ({
        members: state.members.map(member =>
          member.id === memberId
            ? {
                ...member,
                expenses: member.expenses.map(expense =>
                  expense.id === expenseId
                    ? { ...expense, ...updates }
                    : expense
                )
              }
            : member
        )
      })),
      
      removeExpense: (memberId, expenseId) => set((state) => ({
        members: state.members.map(member =>
          member.id === memberId
            ? {
                ...member,
                expenses: member.expenses.filter(expense => expense.id !== expenseId)
              }
            : member
        )
      })),
      
      addSavingsGoal: (memberId, goal) => set((state) => ({
        members: state.members.map(member =>
          member.id === memberId
            ? {
                ...member,
                savingsGoals: [...member.savingsGoals, { ...goal, id: nanoid() }]
              }
            : member
        )
      })),
      
      updateSavingsGoal: (memberId, goalId, updates) => set((state) => ({
        members: state.members.map(member =>
          member.id === memberId
            ? {
                ...member,
                savingsGoals: member.savingsGoals.map(goal =>
                  goal.id === goalId
                    ? { ...goal, ...updates }
                    : goal
                )
              }
            : member
        )
      })),
      
      removeSavingsGoal: (memberId, goalId) => set((state) => ({
        members: state.members.map(member =>
          member.id === memberId
            ? {
                ...member,
                savingsGoals: member.savingsGoals.filter(goal => goal.id !== goalId)
              }
            : member
        )
      })),
      
      addDebt: (memberId, debt) => set((state) => ({
        members: state.members.map(member =>
          member.id === memberId
            ? {
                ...member,
                debts: [...member.debts, { ...debt, id: nanoid() }]
              }
            : member
        )
      })),
      
      updateDebt: (memberId, debtId, updates) => set((state) => ({
        members: state.members.map(member =>
          member.id === memberId
            ? {
                ...member,
                debts: member.debts.map(debt =>
                  debt.id === debtId
                    ? { ...debt, ...updates }
                    : debt
                )
              }
            : member
        )
      })),
      
      removeDebt: (memberId, debtId) => set((state) => ({
        members: state.members.map(member =>
          member.id === memberId
            ? {
                ...member,
                debts: member.debts.filter(debt => debt.id !== debtId)
              }
            : member
        )
      }))
    }),
    {
      name: 'household-storage'
    }
  )
);